import { apiClient } from '@/shared/api/client';
import type { LabelImage } from '@/shared/lib/printLabels';
import type { PagedResult } from '@/shared/api/paged';

export interface Product {
  id: string;
  name: string;
  costPrice: number;
  salePrice: number;
  minSalePrice: number;
  quantity: number;
  minThreshold: number;
  unit: number;
  unitName: string;
  categoryId: number | null;
  categoryName: string | null;
  isTemporary: boolean;
  isInStock: boolean;
  isLowStock: boolean;
  imageUrl: string | null;
  hidePriceFromSellers: boolean;
  sku: string | null;
  /** Zavod shtrix-kodi — skaner shu bo'yicha topadi. Market ichida yagona. */
  barcode: string | null;
  /** Seller-visible short description (Товары "Описание"). */
  description: string | null;
  /** Hidden from POS / seller catalog (still in reports). Distinct from hidePriceFromSellers. */
  isHidden: boolean;
  /** «Место на складе» — storage location (МЕСТО column + seller card). */
  warehouseLocation: string | null;
  /** Склад «ПОСЛ. ПРИХОД» — last accepted goods-receipt date + number (null = none). */
  lastReceiptAt: string | null;
  lastReceiptNumber: number | null;
}

/** Seller product card stats (supplier / last receipt / sold this month). */
export interface ProductStats {
  supplierName: string | null;
  lastReceiptAt: string | null;
  lastReceiptNumber: number | null;
  soldThisMonth: number;
}

export interface ProductCategory {
  id: number;
  name: string;
  description?: string | null;
  icon?: string | null;
  isActive?: boolean;
  productCount?: number;
}

export interface CategoryBody {
  name: string;
  description?: string | null;
  icon?: string | null;
}

/** GET /Products/summary — warehouse KPI tiles. */
export interface WarehouseSummary {
  positions: number;
  /** null when the caller lacks data.costPrice. */
  stockValue: number | null;
  lowStock: number;
  outOfStock: number;
}

export interface UnitInfo {
  value: number;
  nameUz: string;
  nameEn: string;
  nameRu: string;
}

export interface ProductQuery {
  page?: number;
  size?: number;
  search?: string;
  categoryId?: number | null;
  lowStockOnly?: boolean;
  /** Admin Товары/Склад pass true to see hidden products; POS/seller omit it. */
  includeHidden?: boolean;
}

export interface CreateProductBody {
  name: string;
  sku?: string | null;
  barcode?: string | null;
  salePrice: number;
  minSalePrice: number;
  costPrice: number;
  quantity: number;
  minThreshold: number;
  unit: number;
  categoryId: number | null;
  isTemporary: boolean;
  hidePriceFromSellers: boolean;
  description?: string | null;
}

export interface StocktakeItem {
  productId: string;
  countedQty: number;
}

export interface StocktakeResult {
  adjustedCount: number;
  lines: {
    productId: string;
    name: string;
    before: number;
    counted: number;
    variance: number;
  }[];
}

export const productsApi = {
  listPaged: async (q: ProductQuery): Promise<PagedResult<Product>> => {
    const { data } = await apiClient.get<PagedResult<Product>>('/Products/GetAllProductsPaged', {
      params: {
        page: q.page ?? 1,
        size: q.size ?? 50,
        search: q.search || undefined,
        categoryId: q.categoryId ?? undefined,
        lowStockOnly: q.lowStockOnly || undefined,
        includeHidden: q.includeHidden || undefined,
      },
    });
    return data;
  },

  /**
   * Bo'sh ichki EAN-13 kod so'raydi. Server hech narsa saqlamaydi — kod
   * bazaga forma saqlanganda yoziladi, ya'ni «Bekor» bosilsa hech nima
   * o'zgarmaydi va yangi (hali saqlanmagan) tovar uchun ham ishlaydi.
   */
  suggestBarcode: async (): Promise<string> => {
    const { data } = await apiClient.get<{ barcode: string }>('/Products/barcode/suggest');
    return data.barcode;
  },

  /**
   * Bitta yorliqning ko'rinishi (PNG). Bazaga tegmaydi — barcha ma'lumot
   * so'rovda ketadi, shuning uchun ko'rinishni ochish yoki o'lchamni
   * almashtirish hech narsani o'zgartirmaydi.
   */
  labelPreview: async (body: {
    name: string;
    sku?: string | null;
    barcode?: string | null;
    widthMm: number;
    heightMm: number;
  }): Promise<Blob> => {
    const { data } = await apiClient.post<Blob>('/Products/labels/preview', body, {
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Yorliqlar RASM bo'lib — aniq o'lchamda chop etish uchun.
   *
   * <p>PDF yo'li bilan bir xil tayyorgarlikdan o'tadi (kodsiz tovarga kod
   * biriktiriladi), farqi faqat natijada: rasmni aniq `@page` o'lchamli
   * sahifaga qo'yish mumkin va brauzer uni masshtablamaydi.</p>
   */
  labelImages: async (
    items: { productId: string; copies: number }[],
    widthMm: number,
    heightMm: number,
  ): Promise<LabelImage[]> => {
    const { data } = await apiClient.post<LabelImage[]>('/Products/labels/images', {
      items,
      widthMm,
      heightMm,
    });
    return data;
  },

  /**
   * Yorliqlar printerning O'Z tilida (TSPL) — eng aniq yo'l.
   *
   * <p>Rulon o'lchami bu yerda YUBORILMAYDI: uni server do'kon
   * sozlamasidan oladi. Manba bitta bo'lishi kerak — aks holda sozlama
   * bilan so'rov bir-biriga zid qiymat berib turardi.</p>
   *
   * <p>Baytlar qobiq orqali printerga xom holda ketadi: na drayver, na
   * rasterlash, shtrix kodni printerning o'zi chizadi.</p>
   */
  labelsTspl: async (items: { productId: string; copies: number }[]): Promise<ArrayBuffer> => {
    const { data } = await apiClient.post<ArrayBuffer>(
      '/Products/labels/tspl',
      { items },
      { responseType: 'arraybuffer' },
    );
    return data;
  },

  /**
   * Sinov yorlig'i (ramka + markaziy xoch) — TSPL da. O'lcham berilsa
   * SAQLANMAGAN qiymat sinaladi: texnik yangi rulonni saqlashdan oldin
   * tekshiradi.
   */
  labelTestTspl: async (roll?: {
    widthMm: number;
    heightMm: number;
    gapMm: number;
    offsetMm: number;
  }): Promise<ArrayBuffer> => {
    const { data } = await apiClient.get<ArrayBuffer>('/Products/labels/test/tspl', {
      params: roll,
      responseType: 'arraybuffer',
    });
    return data;
  },

  /**
   * Tirqish sensori kalibrovkasi — rulon almashtirilganda birinchi shu.
   * Bir-ikki yorliq bo'sh chiqadi, bu normal.
   */
  labelCalibrate: async (roll?: {
    widthMm: number;
    heightMm: number;
    gapMm: number;
  }): Promise<ArrayBuffer> => {
    const { data } = await apiClient.get<ArrayBuffer>('/Products/labels/calibrate', {
      params: roll,
      responseType: 'arraybuffer',
    });
    return data;
  },

  /**
   * Yorliq PDF i. Har nusxa — alohida sahifa (yorliq printeri sahifadan keyin
   * qog'ozni uzadi). Kodsiz tovarlarga server kod o'zi biriktiradi.
   */
  labelsPdf: async (
    items: { productId: string; copies: number }[],
    widthMm: number,
    heightMm: number,
  ): Promise<Blob> => {
    const { data } = await apiClient.post<Blob>(
      '/Products/labels',
      { items, widthMm, heightMm },
      { responseType: 'blob' },
    );
    return data;
  },

  /** Inline edit of a single field (sale price / min stock / visibility). Only
   *  the given fields change; the server audits price & visibility changes. */
  patch: async (
    id: string,
    body: { salePrice?: number; minThreshold?: number; isHidden?: boolean },
  ): Promise<Product> => {
    const { data } = await apiClient.patch<Product>(`/Products/PatchProduct/${id}`, body);
    return data;
  },

  create: async (body: CreateProductBody): Promise<Product> => {
    const { data } = await apiClient.post<Product>('/Products/CreateProduct', body);
    return data;
  },

  update: async (
    id: string,
    body: Omit<CreateProductBody, 'quantity'> & { quantity: number | null },
  ): Promise<Product> => {
    // Controller uses [Route("api/[controller]/[action]")] + [HttpPut("{id}")]
    // → real route keeps the action segment.
    const { data } = await apiClient.put<Product>(`/Products/UpdateProduct/${id}`, { id, ...body });
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/Products/DeleteProduct/${id}`);
  },

  exportExcel: async (lang: string): Promise<Blob> => {
    const { data } = await apiClient.get('/Products/export', { params: { lang }, responseType: 'blob' });
    return data as Blob;
  },

  /** Warehouse KPI tiles, aggregated server-side (no full-list download). */
  summary: async (): Promise<WarehouseSummary> => {
    const { data } = await apiClient.get<WarehouseSummary>('/Products/summary');
    return data;
  },

  /** Unpaged list (server caps at 5000) — used to compute warehouse stat cards. */
  listAll: async (): Promise<Product[]> => {
    const { data } = await apiClient.get<Product[]>('/Products/GetAllProducts');
    return data;
  },

  units: async (): Promise<UnitInfo[]> => {
    const { data } = await apiClient.get<UnitInfo[]>('/Products/GetUnits/units');
    return data;
  },

  stocktake: async (items: StocktakeItem[]): Promise<StocktakeResult> => {
    const { data } = await apiClient.post<StocktakeResult>('/Products/stocktake', { items });
    return data;
  },

  /** Stock-movement ledger for one product ("Движение товара"), newest first. */
  movements: async (productId: string, limit = 50): Promise<StockMovement[]> => {
    const { data } = await apiClient.get<StockMovement[]>(`/Products/${productId}/movements`, {
      params: { limit },
    });
    return data;
  },

  stats: async (productId: string): Promise<ProductStats> => {
    const { data } = await apiClient.get<ProductStats>(`/Products/${productId}/stats`);
    return data;
  },
};

/** One line of the stock-movement ledger (see Buildix.Domain StockMovement). */
export interface StockMovement {
  id: string;
  /** InitialStock | Purchase | Sale | SaleReversal | Correction */
  type: string;
  delta: number;
  resultingQty: number;
  /** Source document number (Ч-#### / З-###) or null. */
  refNumber: number | null;
  userName: string | null;
  comment: string | null;
  createdAt: string;
}

export const categoriesApi = {
  list: async (): Promise<ProductCategory[]> => {
    const { data } = await apiClient.get<ProductCategory[]>('/ProductCategories/GetAllCategories');
    return data;
  },

  create: async (body: CategoryBody): Promise<ProductCategory> => {
    const { data } = await apiClient.post<ProductCategory>('/ProductCategories/CreateCategory', body);
    return data;
  },

  update: async (id: number, body: CategoryBody & { isActive: boolean }): Promise<ProductCategory> => {
    const { data } = await apiClient.put<ProductCategory>('/ProductCategories/UpdateCategory', { id, ...body });
    return data;
  },

  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/ProductCategories/DeleteCategory/${id}`);
  },
};
